import { Button, Form, FormInstance, Input, InputNumber, Select, Switch } from 'antd'
import { editBlackSVG } from 'assets/svg/edit'
import { EditButtonRowWrapper, EditButtonWrapper } from 'atom/tenants'
import { MasterAccountSelector } from 'components/selector/master-account-selector'
import { RevenueSharePartnerSelector } from 'components/selector/revenue-share-partner-selector'
import { useListingCommissionFeeTranslation } from 'hooks/translation/useListingCommissionFeeTrans'
import { useTenantTranslation } from 'hooks/translation/useTenantTranslation'
import { MasterAccountRef } from 'models/master-account'
import { Tagging } from 'models/tagging'
import { Tenant } from 'models/tenant'
import { useAppState } from 'state'
import configProvider from 'config'

interface TenantAccountInfoProps {
  form: FormInstance
  tenant: Tenant
  disabled: boolean
  masterAccount?: MasterAccountRef
  setMasterAccount: (masterAccount: MasterAccountRef | undefined) => void
  setEditAccountDetailModal: (masterAccount: boolean) => void
}

const TenantAccountInfo: React.FC<TenantAccountInfoProps> = ({
  form,
  tenant,
  disabled,
  masterAccount,
  setMasterAccount,
  setEditAccountDetailModal,
}) => {
  const { IsTablet, IsMobile } = useAppState()
  const isTablet = IsTablet()
  const isMobile = IsMobile()
  const {
    accountText,
    taxRegistrationText,
    taxRegistrationOptionText,
    taxRegistrationOptionNotRegisteredText,
    taxRegistrationOptionJointElectedText,
    remittanceEmailText,
    remittanceFrequencyText,
    editAccountDetailsText,
    enableFeatureText,
    revenueShareEnabledText,
    revenueShareRateText,
    revenueShareRatePlaceholderText,
  } = useTenantTranslation()

  const taxRegistrationOptions = [
    { key: 'registered', value: 'registered', label: taxRegistrationOptionText },
    { key: 'not_registered', value: 'not_registered', label: taxRegistrationOptionNotRegisteredText },
    { key: 'joint_elected', value: 'joint_elected', label: taxRegistrationOptionJointElectedText },
  ]

  const { partnerText, revenuePartnerPercentageText } = useListingCommissionFeeTranslation()

  const handleRevenueSharePartnerChange = (revSharePartner: Tagging | null) => {
    form.setFieldsValue({ revSharePartner: revSharePartner ?? null })
    form.setFieldsValue({ taxRegistration: 'not_registered' })
    form.setFieldsValue({ revSharePartnerPercent: 1 })
  }

  const handleRevenueShareEnabledChange = (enabled: boolean) => {
    form.setFieldsValue({ revenueShareEnabled: enabled })
    if (enabled) {
      form.setFieldsValue({ revenueShareRate: 0.06 })
    }
  }

  const isTaxRegistered = (getFieldValue: (field: string) => any): boolean => {
    const taxRegistration = getFieldValue('taxRegistration')
    return taxRegistration === 'registered' || taxRegistration === 'joint_elected'
  }

  return (
    <>
      <EditButtonRowWrapper isTablet={isTablet} isMobile={isMobile} spaceBetween="-20">
        <Form.Item id="masterAccount" label={accountText} className="align-center">
          <MasterAccountSelector
            masterAccount={masterAccount}
            onOptionChange={(selectedMasterAccount) => setMasterAccount(selectedMasterAccount)}
            disabled={disabled}
          />
          {isMobile || isTablet ? (
            <EditButtonWrapper>
              <Button
                type="primary"
                disabled={disabled}
                icon={editBlackSVG}
                onClick={() => setEditAccountDetailModal(true)}
              />
            </EditButtonWrapper>
          ) : (
            <Button disabled={disabled} type="primary" onClick={() => setEditAccountDetailModal(true)}>
              {editAccountDetailsText}
            </Button>
          )}
        </Form.Item>
      </EditButtonRowWrapper>

      <Form.Item
        name="remittanceEmails"
        label={remittanceEmailText}
        rules={[{ required: false }]}
        className="align-center"
      >
        <Input disabled={true} />
      </Form.Item>
      <Form.Item
        name="remittanceFrequency"
        label={remittanceFrequencyText}
        rules={[{ required: false }]}
        className="align-center"
      >
        <Input disabled={true} />
      </Form.Item>

      <Form.Item name="taxRegistration" label={taxRegistrationText} className="align-center">
        <Select disabled={disabled} allowClear>
          {taxRegistrationOptions.map((option) => (
            <Select.Option key={option.key} value={option.value} label={option.label}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) => prevValues.taxRegistration !== currentValues.taxRegistration}
      >
        {({ getFieldValue }) =>
          isTaxRegistered(getFieldValue) ? (
            <Form.Item name="taxNumber" label="Tax Number" rules={[{ required: true }]}>
              <Input disabled={disabled} />
            </Form.Item>
          ) : null
        }
      </Form.Item>

      <Form.Item className="align-center" name="revSharePartner" label={partnerText}>
        <RevenueSharePartnerSelector
          defaultSelectedPartner={tenant?.revSharePartner}
          disabled={disabled}
          placeholder={partnerText}
          onSelect={handleRevenueSharePartnerChange}
        />
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) => prevValues.revSharePartner !== currentValues.revSharePartner}
      >
        {({ getFieldValue }) =>
          getFieldValue('revSharePartner') ? (
            <Form.Item className="align-center" name="revSharePartnerPercent" label={revenuePartnerPercentageText}>
              <InputNumber disabled={disabled} defaultValue={0} placeholder="12" precision={0} min={1} max={100} />
            </Form.Item>
          ) : null
        }
      </Form.Item>

      {configProvider.config.network === 'itsElectric' && (
        <>
          <Form.Item className="align-center" name="revenueShareEnabled" label={revenueShareEnabledText} valuePropName="checked">
            <Switch disabled={disabled} onChange={handleRevenueShareEnabledChange} />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.revenueShareEnabled !== currentValues.revenueShareEnabled}
          >
            {({ getFieldValue }) =>
              getFieldValue('revenueShareEnabled') ? (
                <Form.Item
                  className="align-center"
                  name="revenueShareRate"
                  label={revenueShareRateText}
                  initialValue={0.06}
                >
                  <Input
                    disabled={true}
                    value={0.06}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </>
      )}

    </>
  )
}

export default TenantAccountInfo
